import { CloseOutlined } from '@ant-design/icons';
import { Tag as TagDTO } from '@dto/tag.dto';
import { TagValue } from '@dto/tagValue.dto';
import { Button, Input, Select, type InputRef, type RefSelectProps } from 'antd';
import { FC, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Log } from 'src/service/log.service';
import { fetchTagValuesByCategory } from '../api/tagApi';
import { FILTER_TYPE_LABELS, HEALTH_STATUS_OPTIONS } from '../constants';
import { createFilterTag } from '../domain/filterFactory';
import type { FilterTag, FilterTagType, SelectionType, SubsystemClassificationItem } from '../search.types';
import '../styles/FilterBadge.less';

interface FilterBadgeProps {
  filter: FilterTag;
  isFocused: boolean;
  focusTarget?: 'editor' | 'remove';
  isRemoving?: boolean;
  onRemove: () => void;
  onSave: (updatedFilter: FilterTag) => void;
  onSubmit?: () => void;
  onKeyDown: (e: KeyboardEvent<Element>) => void;
  availableTags: TagDTO[];
  availableTagsCategories: TagDTO[];
  availableArchitecture: SubsystemClassificationItem[];
}

const getFilterTypeDisplayLabel = (filter: FilterTag, labels: Partial<Record<FilterTagType | SelectionType, string>> = {}): string => {
  if (filter.type === 'class_subsystem' || filter.type === 'hw_type_subsystem') {
    return labels.technology || 'Technology';
  }
  if (filter.type === 'tag_category_tag') {
    return labels.tag_category || 'Tag Category';
  }
  return labels[filter.type] || filter.label;
};

const TEXT_FILTER_TYPES = new Set(['search', 'regex', 'ip', 'mac', 'wwn', 'volid']);

export const FilterBadge: FC<FilterBadgeProps> = ({
  filter,
  isFocused,
  focusTarget = 'editor',
  isRemoving,
  onRemove,
  onSave,
  onSubmit,
  onKeyDown,
  availableTags,
  availableTagsCategories,
  availableArchitecture,
}) => {
  const isTextFilter = TEXT_FILTER_TYPES.has(filter.type);
  const isHealthStatus = filter.type === 'health_status';
  const isTag = filter.type === 'tag';
  const isTagCategory = filter.type === 'tag_category_tag';
  const isTechnology = filter.type === 'class_subsystem' || filter.type === 'hw_type_subsystem';
  const isDualSelect = isTechnology || isTagCategory;

  const inputRef = useRef<InputRef>(null);
  const firstSelectRef = useRef<RefSelectProps>(null);
  const secondSelectRef = useRef<RefSelectProps>(null);
  const removeBtnRef = useRef<HTMLButtonElement>(null);
  const typeLabel = getFilterTypeDisplayLabel(filter, FILTER_TYPE_LABELS);

  const [editValue, setEditValue] = useState(filter.value);
  const [selectedFirst, setSelectedFirst] = useState<string | null>(() =>
    isDualSelect ? filter.value.split(':')[0] || null : null
  );
  const [selectedSecond, setSelectedSecond] = useState<string | null>(() =>
    isDualSelect ? filter.value.split(':')[1] || null : null
  );
  const [categoryTags, setCategoryTags] = useState<TagValue[]>([]);
  const [secondSelectOpen, setSecondSelectOpen] = useState(false);

  // --- Sync state with filter prop ---

  useEffect(() => {
    setEditValue(filter.value);
  }, [filter.value]);

  useEffect(() => {
    if (!isDualSelect) return;
    const [first, second] = filter.value.split(':');
    setSelectedFirst(first || null);
    setSelectedSecond(second || null);
  }, [filter.value, isDualSelect]);

  // --- Memoized options ---

  const tagOptions = useMemo(() =>
    [...availableTags]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((tag) => ({ label: tag.label, value: tag.label })),
    [availableTags]
  );

  const categoryOptions = useMemo(() =>
    [...availableTagsCategories]
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((cat) => ({ label: cat.label, value: cat.label })),
    [availableTagsCategories]
  );

  const categoryTagOptions = useMemo(() => [
    { label: 'All', value: '*' },
    ...[...categoryTags]
      .sort((a, b) => (a.value || '').localeCompare(b.value || ''))
      .map((tag) => ({ label: tag.value, value: tag.value })),
  ], [categoryTags]);

  const technologyOptions = useMemo(() => {
    const classMap = new Map<string, string>();
    const hwTypeMap = new Map<string, string>();
    for (const item of availableArchitecture) {
      if ('class' in item && item.class && !classMap.has(item.class)) {
        classMap.set(item.class, item.classLabel || item.class);
      } else if ('hwType' in item && item.hwType && !hwTypeMap.has(item.hwType)) {
        hwTypeMap.set(item.hwType, item.hwTypeLabel || item.hwType);
      }
    }
    return [
      ...Array.from(classMap.entries()).map(([v, l]) => ({ label: l, value: v })),
      ...Array.from(hwTypeMap.entries()).map(([v, l]) => ({ label: l, value: v })),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [availableArchitecture]);

  const subsystemOptions = useMemo(() => {
    if (!selectedFirst) return [];
    const unique = new Map<string, string>();
    for (const item of availableArchitecture) {
      const match =
        ('class' in item && item.class === selectedFirst) ||
        ('hwType' in item && item.hwType === selectedFirst);
      if (match && item.subsystem && !unique.has(item.subsystem)) {
        unique.set(item.subsystem, item.subsystemLabel || item.subsystem);
      }
    }
    return Array.from(unique.entries())
      .map(([v, l]) => ({ label: l, value: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableArchitecture, selectedFirst]);

  // --- API ---

  useEffect(() => {
    if (!isTagCategory || !selectedFirst) return;
    const abortController = new AbortController();
    void (async () => {
      try {
        const tags = await fetchTagValuesByCategory(
          selectedFirst,
          abortController.signal
        );
        setCategoryTags(tags);
      } catch (error) {
        if (!abortController.signal.aborted) {
          Log.error('Failed to load tags by category', error);
          setCategoryTags([]);
        }
      }
    })();
    return () => abortController.abort();
  }, [isTagCategory, selectedFirst]);

  // --- Focus management ---

  useEffect(() => {
    if (!isFocused) return;
    if (focusTarget === 'remove') {
      removeBtnRef.current?.focus();
    } else if (isTextFilter) {
      inputRef.current?.focus();
    } else {
      firstSelectRef.current?.focus();
    }
  }, [isFocused, isTextFilter, focusTarget]);

  // --- Handlers ---

  const handleSaveText = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== filter.value) {
      onSave(
        createFilterTag(filter.type, trimmed, {
          availableArchitecture,
          availableTagsCategories,
          typeLabels: FILTER_TYPE_LABELS,
        })
      );
    } else {
      setEditValue(filter.value);
    }
  }, [editValue, filter, onSave, availableArchitecture, availableTagsCategories]);

  const handleSelectSave = useCallback((newValue: string) => {
    if (newValue !== filter.value) {
      onSave(
        createFilterTag(filter.type, newValue, {
          availableArchitecture,
          availableTagsCategories,
          typeLabels: FILTER_TYPE_LABELS,
        })
      );
    }
  }, [filter, onSave, availableArchitecture, availableTagsCategories]);

  const handleInputKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSaveText();
      onSubmit?.();
    } else if (e.key === 'Escape') {
      setEditValue(filter.value);
      onKeyDown(e);
    } else if (e.key === 'Tab') {
      handleSaveText();
      if (e.shiftKey) {
        onKeyDown(e);
      } else {
        e.preventDefault();
        setTimeout(() => removeBtnRef.current?.focus(), 0);
      }
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const nativeInput = inputRef.current?.input;
      if (nativeInput) {
        const len = nativeInput.value.length;
        if (!e.shiftKey) {
          const pos = e.key === 'Home' ? 0 : len;
          nativeInput.setSelectionRange(pos, pos);
        } else {
          const { selectionStart, selectionEnd, selectionDirection } = nativeInput;
          const anchor = selectionStart === selectionEnd
            ? (selectionStart ?? 0)
            : selectionDirection === 'backward'
              ? (selectionEnd ?? len)
              : (selectionStart ?? 0);
          if (e.key === 'Home') {
            nativeInput.setSelectionRange(0, anchor, 'backward');
          } else {
            nativeInput.setSelectionRange(anchor, len, 'forward');
          }
        }
      }
    }
  }, [handleSaveText, onSubmit, onKeyDown, filter.value]);

  const handleRemoveBtnKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        e.preventDefault();
        if (isDualSelect && selectedFirst) {
          secondSelectRef.current?.focus();
        } else if (isTextFilter) {
          inputRef.current?.focus();
        } else {
          firstSelectRef.current?.focus();
        }
      } else {
        onKeyDown(e);
      }
    } else if (e.key === 'Escape') {
      onKeyDown(e);
    }
  }, [isDualSelect, selectedFirst, isTextFilter, onKeyDown]);

  const handleFirstSelectKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      onKeyDown(e);
    }
  }, [onKeyDown]);

  const focusSecondSelect = () => {
    setSecondSelectOpen(false);
    setTimeout(() => {
      secondSelectRef.current?.focus();
      setSecondSelectOpen(true);
    }, 50);
  };

  // --- Render helpers ---

  const renderDualSelect = (
    firstOptions: { label: string; value: string; }[],
    secondOptions: { label: string; value: string; }[],
    firstPlaceholder: string,
    secondPlaceholder: string,
  ) => (
    <span className='xm-filter-badge__editor-dual'>
      <Select
        ref={firstSelectRef}
        size='small'
        value={selectedFirst}
        options={firstOptions}
        placeholder={firstPlaceholder}
        onSelect={(val: string) => {
          setSelectedFirst(val);
          setSelectedSecond(null);
          focusSecondSelect();
        }}
        onKeyDown={handleFirstSelectKeyDown}
        showSearch
        optionFilterProp='label'
      />
      {selectedFirst && (
        <Select
          ref={secondSelectRef}
          size='small'
          value={selectedSecond}
          options={secondOptions}
          placeholder={secondPlaceholder}
          open={secondSelectOpen}
          onOpenChange={setSecondSelectOpen}
          onChange={(val: string) => {
            setSecondSelectOpen(false);
            handleSelectSave(`${selectedFirst}:${val}`);
          }}
          onBlur={() => {
            setSecondSelectOpen(false);
            if (selectedSecond) return;
            if (secondOptions.length > 0) {
              const fallback = secondOptions[0].value;
              handleSelectSave(`${selectedFirst}:${fallback}`);
            } else {
              const [prevFirst, prevSecond] = filter.value.split(':');
              setSelectedFirst(prevFirst || null);
              setSelectedSecond(prevSecond || null);
            }
          }}
          showSearch
          optionFilterProp='label'
        />
      )}
    </span>
  );

  const renderEditor = () => {
    if (isTextFilter) {
      return (
        <Input
          ref={inputRef}
          size='small'
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onBlur={handleSaveText}
          onFocus={() => {
            requestAnimationFrame(() => {
              const el = inputRef.current?.input;
              if (el && document.contains(el)) {
                el.setSelectionRange(el.value.length, el.value.length);
              }
            });
          }}
        />
      );
    }

    if (isHealthStatus) {
      return (
        <Select
          ref={firstSelectRef}
          size='small'
          value={filter.value}
          options={HEALTH_STATUS_OPTIONS}
          onChange={handleSelectSave}
          onKeyDown={handleFirstSelectKeyDown}
        />
      );
    }

    if (isTag) {
      return (
        <Select
          ref={firstSelectRef}
          size='small'
          value={filter.value}
          options={tagOptions}
          onChange={handleSelectSave}
          onKeyDown={handleFirstSelectKeyDown}
          showSearch
          filterOption={(input, option) =>
            option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
          }
        />
      );
    }

    if (isTechnology) {
      return renderDualSelect(
        technologyOptions,
        subsystemOptions,
        'Technology...',
        'Subsystem...',
      );
    }

    if (isTagCategory) {
      return renderDualSelect(
        categoryOptions,
        categoryTagOptions,
        'Category...',
        'Tag...',
      );
    }

    return null;
  };

  return (
    <div
      className={`xm-filter-badge${isRemoving ? ' xm-filter-badge--removing' : ''}`}
      data-testid='search-filter-badge'
    >
      <span className='xm-filter-badge__type' data-testid='search-filter-badge-type'>{typeLabel}</span>
      <span className='xm-filter-badge__editor'>
        {renderEditor()}
      </span>
      <span className='xm-filter-badge__actions'>
        <Button
          ref={removeBtnRef}
          type='link'
          className='hover-danger'
          icon={<CloseOutlined />}
          onMouseDown={(e) => e.preventDefault()}
          onKeyDown={handleRemoveBtnKeyDown}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        />
      </span>
    </div>
  );
};
